<script setup lang="ts">
import { computed } from "vue";
import { isUidOfTypes } from "@/helpers/utility/Id";
import { formatRoundName } from "../simulationRound/useRoundTime";
import { getContrastColor } from "@/helpers/utility/Color";

import { useCondition } from "./useCondition";
import { useSceneTree } from "../scene/sceneTree";
import { useSettingsStore } from "@editor/store/settings.store";

import ConditionOperatorIcon from "./ConditionOperatorIcon.vue";
import ConditionLabelTime from "./ConditionLabelTime.vue";

interface Props {
  conditionId: number;
  activatorHidden?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  activatorHidden: false
});

const sceneTree = useSceneTree();
const ctx = useCondition(() => props.conditionId);

const settingsStore = useSettingsStore();

const activatorSceneNode = computed(() => {
  if (!ctx.activatorItem) return null;
  const uid = ctx.activatorItem.uid;
  if (!isUidOfTypes(uid, ["target", "resource", "index"])) return null;
  const treeNode = sceneTree.elementNodes.get(uid);
  if (!treeNode) return null;
  return treeNode.node;
});

const activatorBackground = computed(() => {
  const sceneNode = activatorSceneNode.value;
  if (!sceneNode) return undefined;
  if ("fill" in sceneNode) {
    return sceneNode.fill || undefined;
  }
  return undefined;
});
const activatorTextColor = computed(() => {
  const sceneNode = activatorSceneNode.value;
  if (
    sceneNode &&
    "textColor" in sceneNode &&
    sceneNode.textColor
  ) {
    return sceneNode.textColor;
  }
  if (!activatorBackground.value) return undefined;
  return getContrastColor(activatorBackground.value);
});

const comparedSceneNode = computed(() => {
  if (!ctx.comparedItem) return null;
  const uid = ctx.comparedItem.uid;
  if (!isUidOfTypes(uid, ["target", "resource", "index"])) return null;
  const treeNode = sceneTree.elementNodes.get(uid);
  if (!treeNode) return null;
  return treeNode.node;
});
const comparedBackground = computed(() => {
  const sceneNode = comparedSceneNode.value;
  if (!sceneNode) return undefined;
  if ("fill" in sceneNode) {
    return sceneNode.fill || undefined;
  }
  return undefined;
});
const comparedTextColor = computed(() => {
  const sceneNode = comparedSceneNode.value;
  if (
    sceneNode &&
    "textColor" in sceneNode &&
    sceneNode.textColor
  ) {
    return sceneNode.textColor;
  }
  if (!comparedBackground.value) return undefined;
  return getContrastColor(comparedBackground.value);
});

function formatRoundIndex(index: number) {
  return formatRoundName(
    settingsStore.simulationDurationType,
    settingsStore.simulationRoundUnit,
    settingsStore.simulationRoundLength,
    index,
    settingsStore.simulationTimeStart,
    false
  );
}

const unit = computed(() => {
  if (ctx.activatorItem && "unit" in ctx.activatorItem) {
    return ctx.activatorItem.unit;
  }
  return "";
});

const roundUnit = computed(() => settingsStore.simulationRoundUnit);

function areStartAndEndDifferent(
  startType: string | null,
  start: number | null,
  endType: string | null,
  end: number | null
) {
  if (startType != endType) return true;
  if (start != end) return true;
  return false;
}

const usedTimeForm = computed(() => {
  if (ctx.activatorTimeForm == "point") return "point";

  if (ctx.condition.activatorEndType == null) return "point";

  if (
    areStartAndEndDifferent(
      ctx.condition.activatorStartType,
      ctx.condition.activatorStart,
      ctx.condition.activatorEndType,
      ctx.condition.activatorEnd
    )
  ) {
    return "range";
  }

  return "point";
});

const comparedValue = computed(() => {
  if (ctx.condition.operator == "?" || ctx.condition.operator == "!") {
    return 1;
  }
  if (ctx.condition.comparedType == "value") {
    return ctx.condition.comparedValue || 0;
  }
  return 0;
});
const pluralCount = computed(() => {
  if (ctx.condition.operator == ">" || ctx.condition.operator == ">=") {
    return comparedValue.value + 1;
  }
  return comparedValue.value;
});

const activatorProperty = computed(() => {
  if (ctx.condition.activatorProperty == "valueChange") {
    if (ctx.condition.activatorPropertyVariant == "positive") {
      return "increase";
    }
    if (ctx.condition.activatorPropertyVariant == "negative") {
      return "decrease";
    }
  }
  return ctx.condition.activatorProperty;
});
</script>

<template>
  <div class="condition-label">
    <template v-if="ctx.condition?.activatorType == null">
      <div></div>
    </template>

    <template v-else-if="ctx.condition.activatorType == 'round'">
      <div>{{ $t("activatorTypes.round") }}{{ $t(":") }}</div>

      <div v-if="ctx.operatorValid">
        &nbsp;{{ $t(`roundOperators.${ctx.condition.operator}`) }}
      </div>
      <div v-else>&nbsp;___</div>

      <div v-if="ctx.condition.comparedType == null" class="condition-label__missingicon" :title="$t('messages.missingValue')"></div>

      <template v-else-if="ctx.condition.comparedType == 'value'">
        <div v-if="ctx.condition.comparedValue != null">
          <strong>{{ formatRoundIndex(ctx.condition.comparedValue) }}</strong>
        </div>
      </template>

      <template v-else>
        <template v-if="ctx.condition.comparedProperty">
          <div class="condition-label__property">
            {{
              $t(
                `activatorProperties.${ctx.condition.comparedType}.${ctx.condition.comparedProperty}`,
                4
              )
            }}
          </div>
          <span>{{ $t("words.of") }}</span>
        </template>

        <div
          v-if="ctx.comparedItem"
          class="condition-label__item"
          :style="{
            '--bg': comparedBackground,
            '--color': comparedTextColor
          }"
        >
          {{ ctx.comparedItem?.title }}
        </div>
        <div
          v-else-if="ctx.condition.comparedType"
          class="condition-label__type"
        >
          {{ $t(`activatorTypes.${ctx.condition.comparedType}`) }} <div class="condition-label__missingicon" :title="$t('messages.missingValue')"></div>
        </div>
      </template>
    </template>

    <template v-else-if="ctx.condition.activatorType == 'roundRange'">
      <template v-if="usedTimeForm == 'range'">
        <div>{{ $t("activatorTypes.roundRange") }}{{ $t(":") }}</div>
        <i18n-t
          :keypath="
            ctx.condition.operator == '!'
              ? 'labels.conditionLabelRoundRangeNot'
              : 'labels.conditionLabelRoundRange'
          "
          scope="global"
        >
          <template #start>
            <strong v-if="ctx.condition.activatorStart != null">
              {{ formatRoundIndex(ctx.condition.activatorStart) }}
            </strong>
          </template>
          <template #end>
            <strong v-if="ctx.condition.activatorEnd != null">
              {{ formatRoundIndex(ctx.condition.activatorEnd) }}
            </strong>
          </template>
        </i18n-t>
        <div v-if="ctx.condition.operator == null">___?</div>
      </template>
      <template v-else>
        <div>{{ $t("activatorTypes.round") }}{{ $t(":") }}</div>

        <div v-if="ctx.operatorValid">
          &nbsp;{{ $t(`roundOperators.${ctx.condition.operator}`) }}
        </div>
        <div v-else>&nbsp;___</div>

        <div v-if="ctx.condition.activatorStart != null">
          <strong>{{ formatRoundIndex(ctx.condition.activatorStart) }}</strong>
        </div>
      </template>
    </template>

    <template v-else>
      <div v-if="activatorProperty" class="condition-label__property">
        {{
          $t(
            `activatorProperties.${ctx.condition.operator == "!" ? "no." : ""}${
              ctx.condition.activatorType
            }.${activatorProperty}`,
            pluralCount
          )
        }}
      </div>

      <template v-if="!props.activatorHidden">
        <span v-if="activatorProperty && ctx.activatorItem">
          {{ $t("words.of") }}
        </span>
        <div
          v-if="ctx.activatorItem"
          class="condition-label__item"
          :style="{
            '--bg': activatorBackground,
            '--color': activatorTextColor
          }"
        >
          {{ ctx.activatorItem.title }}
        </div>
        <div v-else class="condition-label__type">
          {{ $t(`activatorTypes.${ctx.condition.activatorType}`) }} <div class="condition-label__missingicon" :title="$t('messages.missingValue')"></div>
        </div>
      </template>

      <template v-if="ctx.activatorComplete">
        <condition-label-time
          v-if="ctx.supportsActivatorTime"
          :start-type="ctx.condition.activatorStartType"
          :start="ctx.condition.activatorStart"
          :end-type="ctx.condition.activatorEndType"
          :end="ctx.condition.activatorEnd"
          :time-form="ctx.activatorTimeForm"
          :operator="ctx.condition.operator"
        />

        <condition-operator-icon
          v-if="
            ctx.condition.operator &&
            ctx.condition.operator != '?' &&
            ctx.condition.operator != '!'
          "
          class="condition-label__operatoricon"
          :operator="ctx.condition.operator"
        />
        <!-- <div
          v-if="
            ctx.condition.operator &&
            ctx.condition.operator != '?' &&
            ctx.condition.operator != '!'
          "
          class="condition-label__operator"
        >
          {{ ctx.condition.operator }}
        </div> -->

        <template
          v-if="
            ctx.condition.operator &&
            ctx.condition.operator != '?' &&
            ctx.condition.operator != '!'
          "
        >
          <div
            v-if="ctx.condition.comparedProperty && ctx.comparedItem"
            class="condition-label__property"
          >
            {{
              $t(
                `activatorProperties.${ctx.condition.comparedType}.${ctx.condition.comparedProperty}`,
                4
              )
            }}
          </div>
          <div
            v-if="
              ctx.condition.comparedType == 'value' &&
              ctx.condition.comparedValue != null
            "
            class="condition-label__value"
          >
            <template v-if="ctx.condition.comparedValueType == 'number'">
              {{ ctx.condition.comparedValue }}{{ unit }}
            </template>
            <strong
              v-else-if="ctx.condition.comparedValueType == 'absoluteRound'"
            >
              {{ formatRoundIndex(ctx.condition.comparedValue) }}
            </strong>
            <strong
              v-else-if="ctx.condition.comparedValueType == 'relativeRound'"
            >
              {{ $t(`timeAgo.${roundUnit}`, ctx.condition.comparedValue) }}
            </strong>
          </div>
          <template v-else>
            <span v-if="ctx.condition.comparedProperty && ctx.comparedItem">
              {{ $t("words.of") }}
            </span>
            <div
              v-if="ctx.comparedItem"
              class="condition-label__item"
              :style="{
                '--bg': comparedBackground,
                '--color': comparedTextColor
              }"
            >
              {{ ctx.comparedItem?.title }}
            </div>
            <div
              v-else-if="ctx.condition.comparedType"
              class="condition-label__type"
            >
              {{ $t(`activatorTypes.${ctx.condition.comparedType}`) }} <div class="condition-label__missingicon" :title="$t('messages.missingValue')"></div>
            </div>
            <div v-else class="condition-label__type condition-label__missingicon" :title="$t('messages.missingValue')"></div>

            <condition-label-time
              v-if="ctx.supportsComparedTime && ctx.comparedItem"
              :start-type="ctx.condition.comparedStartType"
              :start="ctx.condition.comparedStart"
              :end-type="ctx.condition.comparedEndType"
              :end="ctx.condition.comparedEnd"
              :time-form="ctx.comparedTimeForm"
            />
          </template>
        </template>
      </template>
    </template>
  </div>
</template>

<style lang="scss">
.condition-label {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;

  &__property,
  &__type,
  &__operator,
  &__value {
    display: inline-block;
  }

  &__item {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    background-color: var(--bg, var(--color-background));
    color: var(--color, var(--color-text));
    border-radius: 5px;
    // box-shadow: 0 0 3px var(--color-shadow-2);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  }

  &__operator {
    font-size: 1.25em;
    font-weight: bold;
    color: var(--color-text);
    padding: 0.125rem 0.25rem;
    display: inline-flex;
    align-items: center;
    margin-top: -5px;
  }

  &__operatoricon {
    flex-shrink: 0;
  }

  &__missingicon {
    display: inline-block;
    color: var(--color-warning-dark);
    background-color: var(--color-warning-light);
    padding: 0.1em 0.5em;
    border: 2px solid var(--color-warning);
    border-radius: 0.5em;
    font-weight: 500;
    font-size: 0.9em;

    &::before {
      content: '?';
    }
  }
}
</style>
